import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import { interpretCommitError } from './interpretCommitError';

/**
 * ViewModel for document field review (HITL).
 *
 * Mirrors useCsvReviewViewModel's contract: commit functions return an OUTCOME
 * STRING so callers branch on the result instead of sniffing phase.
 *   'released'   — 200 with released: true; the document moved to READY
 *   'partial'    — 200 with released: false; remaining_flags still populated
 *   'conflict'   — 409/404; already resolved or gone. Not an error
 *   'incomplete' — 422 incomplete_review; stay open with rows highlighted
 *   'invalid'    — 422 on a specific resolution (corrected_value_required,
 *                  unknown_action)
 *   'stale'      — 422 entity_not_flagged; our snapshot was stale, flags reloaded
 *   'error'      — anything else
 *
 * Flags are identified by `entity_id`, never `field_id` — see DocumentReviewModel.
 */
/**
 * Documents per request. 200 is the server's hard cap, confirmed with backend:
 * the route only enforces `ge=1` and the orchestrator clamps with
 * `min(limit, MAX_PAGE_SIZE)`. Asking for more returns 200 and a cursor with NO
 * error, so this must never be raised expecting a bigger page — it would
 * silently do nothing.
 *
 * Design's call is to load in 200-document increments the reviewer asks for,
 * via the Load more control under the rail, rather than draining every page
 * automatically. So this is both the first page and the size of each increment.
 */
const REVIEW_PAGE_SIZE = 200;

/** Recovery refetch failed: the view is stale and cannot be fixed in place. */
const RELOAD_FAILED = 'This document changed while you were reviewing it, and its '
  + 'fields could not be reloaded. Close and reopen review to get its current state.';
const RELOAD_FAILED_AFTER_SAVE = 'Saved, but this document could not be reloaded. '
  + 'Close and reopen review to get its current state.';

export const useDocumentReviewViewModel = (model) => {
  const [state, setState] = useState(model.getState());

  useEffect(() => {
    const unsubscribe = model.subscribe((newState) => {
      setState(newState);
    });
    setState(model.getState());
    return unsubscribe;
  }, [model]);

  const openReview = useCallback(async (datasetId, datasetName) => {
    model.setDatasetId(datasetId);
    model.setDatasetName(datasetName);
    model.setPhase('loading');
    model.setError(null);

    try {
      const response = await apiService.getDatasetPendingReview(datasetId, {
        limit: REVIEW_PAGE_SIZE,
      });
      model.setReviewData(response);
      model.setPhase('reviewing');
    } catch (error) {
      console.error('[DocumentReviewVM] Error fetching pending review:', error);
      model.setError(error.message || 'Failed to load review data');
      model.setPhase('failed');
    }
  }, [model]);

  /** Load the next cursor page of parked documents, keeping staged work intact. */
  const loadMoreDocuments = useCallback(async () => {
    const { datasetId, nextCursor, loadingMore } = model.getState();
    // Guard re-entry: without this, repeated clicks fire overlapping GETs
    // against the same cursor and each one costs a full page fetch.
    if (!nextCursor || loadingMore) return false;
    model.setLoadingMore(true);
    try {
      const response = await apiService.getDatasetPendingReview(datasetId, {
        limit: REVIEW_PAGE_SIZE,
        cursor: nextCursor,
      });
      model.appendReviewData(response);
      return true;
    } catch (error) {
      console.error('[DocumentReviewVM] Error loading more documents:', error);
      // 422 means the cursor is stale or malformed — documents released since it
      // was issued moved the window. The only valid recovery is the first page;
      // retrying the same cursor would fail identically.
      if (error.status === 422) {
        try {
          const fresh = await apiService.getDatasetPendingReview(datasetId, { limit: REVIEW_PAGE_SIZE });
          model.setReviewData(fresh, { preserveCommitted: true });
          model.setError('The review list moved on while you were working. Reloaded from the start.');
          return true;
        } catch (retryError) {
          console.error('[DocumentReviewVM] First-page refetch failed:', retryError);
          model.setError(retryError.message || 'Failed to reload the review list');
          return false;
        }
      }
      model.setError(error.message || 'Failed to load more documents');
      return false;
    } finally {
      model.setLoadingMore(false);
    }
  }, [model]);

  const stageResolution = useCallback((documentId, entityId, action, correctedValue) => {
    model.stageResolution(documentId, entityId, action, correctedValue);
  }, [model]);

  const stageValue = useCallback((documentId, entityId, rawValue) => (
    model.stageValue(documentId, entityId, rawValue)
  ), [model]);

  const unstageResolution = useCallback((documentId, entityId) => {
    model.unstageResolution(documentId, entityId);
  }, [model]);

  const setActiveIndex = useCallback((index) => {
    model.setActiveIndex(index);
  }, [model]);

  /** Re-fetch one document's flags — used to recover from a stale view. */
  const refreshDocument = useCallback(async (documentId) => {
    try {
      const response = await apiService.getDocumentPendingReview(documentId);
      const flags = response?.flags || [];
      if (flags.length === 0) {
        model.removeDocument(documentId);
      } else {
        model.replaceDocumentFlags(documentId, flags);
      }
      return true;
    } catch (error) {
      // A 404/409 here means it is no longer reviewable, which is a valid state.
      if (error.status === 404 || error.status === 409) {
        model.removeDocument(documentId);
        return true;
      }
      console.error('[DocumentReviewVM] Error refreshing document:', error);
      return false;
    }
  }, [model]);

  /**
   * Commit one document. Every currently-flagged field must be staged — the
   * server recomputes the flag set and rejects an incomplete submission, so we
   * do not attempt to submit a partial document.
   */
  const commitDocument = useCallback(async (documentId) => {
    if (!model.isDocumentComplete(documentId)) {
      model.setError('Resolve every field on this document before saving.');
      return 'incomplete';
    }

    model.setPhase('submitting');
    model.setError(null);
    model.setInvalidFields([]);

    try {
      const resolutions = model.buildResolutions(documentId);
      const response = await apiService.submitDocumentReview(documentId, resolutions);

      // A submit does not always release. Derived fields inherit
      // min(input confidence), so skipping a base field can newly flag a field
      // derived from it. Trust `released` / `remaining_flags` over our own
      // bookkeeping — the server recomputes the set.
      const remaining = response?.remaining_flags || [];
      if (remaining.length > 0) {
        model.replaceDocumentFlags(documentId, remaining);
        model.setPhase('reviewing');
        return 'partial';
      }

      // Not released, yet nothing listed to resolve. Replacing the flags with an
      // empty array here would leave a document that can never be committed
      // (no fields to settle) nor dismissed — so ask the server what it actually
      // holds. refreshDocument drops the document if it really has no flags.
      if (response?.released === false) {
        const reloaded = await refreshDocument(documentId);
        if (!reloaded) {
          model.setError(RELOAD_FAILED_AFTER_SAVE);
        }
        model.setPhase(model.documents.length === 0 ? 'completed' : 'reviewing');
        return 'partial';
      }

      // Only a genuine release counts toward "N documents reviewed".
      model.markDocumentCommitted(documentId);
      model.removeDocument(documentId);
      model.setPhase(model.documents.length === 0 ? 'completed' : 'reviewing');
      return 'released';
    } catch (error) {
      console.error('[DocumentReviewVM] Error submitting document review:', error);

      const verdict = interpretCommitError(error);
      if (verdict.invalidFields) model.setInvalidFields(verdict.invalidFields);
      // Set before the await so the banner is up while the reload is in flight.
      if (verdict.message) model.setError(verdict.message);
      if (verdict.remove) model.removeDocument(documentId);
      // The message above already promised a reload. Do not leave that standing
      // if the refetch failed — the flags on screen are still the stale ones.
      if (verdict.refresh && !(await refreshDocument(documentId))) {
        model.setError(RELOAD_FAILED);
      }

      // Only the queue-changing verdicts can empty it; the rest stay put.
      model.setPhase(
        (verdict.remove || verdict.refresh) && model.documents.length === 0
          ? 'completed'
          : 'reviewing',
      );
      return verdict.outcome;
    }
  }, [model, refreshDocument]);

  const reset = useCallback(() => {
    model.reset();
  }, [model]);

  return {
    ...state,
    activeDocument: model.getActiveDocument(),
    overallProgress: model.getOverallProgress(),
    committedCount: model.getCommittedCount(),
    isDocumentComplete: (documentId) => model.isDocumentComplete(documentId),
    /** Anything staged and uncommitted — the close path confirms on it. */
    hasStagedWork: () => model.hasStagedWork(),
    getStagedResolution: (documentId, entityId) => model.getStagedResolution(documentId, entityId),
    openReview,
    loadMoreDocuments,
    stageResolution,
    stageValue,
    unstageResolution,
    setActiveIndex,
    commitDocument,
    refreshDocument,
    reset,
  };
};

export default useDocumentReviewViewModel;
